import React, { useState, useEffect } from "react";
import Navbar from "../components/common/Navbar";
import JobSearchBar from "../components/JobBoard/JobSearchBar";
import JobFilters from "../components/JobBoard/JobFilters";
import JobRecommendations from "../components/JobBoard/JobRecommendations";
import JobList from "../components/JobBoard/JobList";
import { JobPosting, JobFilters as JobFiltersType } from "../types/job";

const JobBoardPage: React.FC = () => {
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobPosting[]>([]);
  const [recommendedJobs, setRecommendedJobs] = useState<JobPosting[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<JobFiltersType>({
    location: "",
    experience: "",
    jobType: "",
    salary: "",
    company: "",
    skills: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showRecommendations, setShowRecommendations] = useState(true);

  // 임시 데이터 - 실제로는 API에서 가져올 데이터
  useEffect(() => {
    const mockJobs: JobPosting[] = [
      {
        id: "1",
        title: "Senior Frontend Developer",
        company: "테커 테크",
        location: "서울, 대한민국",
        type: "정규직",
        experience: "3-5년",
        salary: "4,000-6,000만원",
        description:
          "React, TypeScript를 활용한 웹 애플리케이션 개발을 담당할 시니어 프론트엔드 개발자를 모집합니다.",
        requirements: ["React", "TypeScript", "JavaScript", "HTML/CSS"],
        benefits: ["원격근무", "성과급", "교육비 지원", "건강보험"],
        postedAt: "2023-10-15",
        deadline: "2023-11-15",
        applicants: 45,
        isRecommended: true,
        companyLogo:
          "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop",
        isBookmarked: false,
      },
      {
        id: "2",
        title: "Full Stack Developer",
        company: "스타트업 코리아",
        location: "서울, 대한민국",
        type: "정규직",
        experience: "2-4년",
        salary: "3,500-5,000만원",
        description:
          "Node.js와 React를 사용한 풀스택 개발을 담당할 개발자를 모집합니다.",
        requirements: ["Node.js", "React", "MongoDB", "AWS"],
        benefits: ["스톡옵션", "유연근무", "개발환경 지원"],
        postedAt: "2023-10-14",
        deadline: "2023-11-10",
        applicants: 32,
        isRecommended: true,
        companyLogo:
          "https://images.unsplash.com/photo-1551434678-e076c223a692?w=100&h=100&fit=crop",
        isBookmarked: false,
      },
      {
        id: "3",
        title: "Backend Developer",
        company: "빅테크 코퍼레이션",
        location: "서울, 대한민국",
        type: "정규직",
        experience: "5-7년",
        salary: "6,000-8,000만원",
        description:
          "대규모 시스템 개발 및 운영을 담당할 백엔드 개발자를 모집합니다.",
        requirements: ["Java", "Spring Boot", "MySQL", "Redis", "Kubernetes"],
        benefits: ["경쟁력 있는 연봉", "성과급", "교육비 지원", "건강검진"],
        postedAt: "2023-10-13",
        deadline: "2023-11-20",
        applicants: 78,
        isRecommended: false,
        companyLogo:
          "https://images.unsplash.com/photo-1599305445771-38ae0c7b2505?w=100&h=100&fit=crop",
        isBookmarked: true,
      },
    ];

    setJobs(mockJobs);
    setFilteredJobs(mockJobs);
    setRecommendedJobs(mockJobs.filter((job) => job.isRecommended));
    setIsLoading(false);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    filterJobs(query, filters);
  };

  const handleFilterChange = (newFilters: JobFiltersType) => {
    setFilters(newFilters);
    filterJobs(searchQuery, newFilters);
  };

  const filterJobs = (query: string, filterOptions: JobFiltersType) => {
    let filtered = jobs;

    // 검색어 필터링
    if (query) {
      filtered = filtered.filter(
        (job) =>
          job.title.toLowerCase().includes(query.toLowerCase()) ||
          job.company.toLowerCase().includes(query.toLowerCase()) ||
          job.description.toLowerCase().includes(query.toLowerCase()) ||
          job.requirements.some((req) =>
            req.toLowerCase().includes(query.toLowerCase())
          )
      );
    }

    // 기타 필터링
    if (filterOptions.location) {
      filtered = filtered.filter((job) =>
        job.location.includes(filterOptions.location)
      );
    }
    if (filterOptions.experience) {
      filtered = filtered.filter(
        (job) => job.experience === filterOptions.experience
      );
    }
    if (filterOptions.jobType) {
      filtered = filtered.filter((job) => job.type === filterOptions.jobType);
    }
    if (filterOptions.company) {
      filtered = filtered.filter((job) =>
        job.company.includes(filterOptions.company)
      );
    }
    if (filterOptions.skills.length > 0) {
      filtered = filtered.filter((job) =>
        filterOptions.skills.some((skill) =>
          job.requirements.some((req) =>
            req.toLowerCase().includes(skill.toLowerCase())
          )
        )
      );
    }

    setFilteredJobs(filtered);
  };

  const handleBookmark = (jobId: string) => {
    setJobs(
      jobs.map((job) =>
        job.id === jobId ? { ...job, isBookmarked: !job.isBookmarked } : job
      )
    );
    setFilteredJobs(
      filteredJobs.map((job) =>
        job.id === jobId ? { ...job, isBookmarked: !job.isBookmarked } : job
      )
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* 검색 바 */}
        <JobSearchBar onSearch={handleSearch} />

        <div className="flex flex-col lg:flex-row gap-8 mt-8">
          {/* 필터 사이드바 */}
          <div className="lg:w-80">
            <JobFilters filters={filters} onFilterChange={handleFilterChange} />
          </div>

          {/* 메인 콘텐츠 */}
          <div className="flex-1">
            {/* 추천 채용공고 */}
            {showRecommendations && recommendedJobs.length > 0 && (
              <JobRecommendations
                jobs={recommendedJobs}
                onBookmark={handleBookmark}
                onClose={() => setShowRecommendations(false)}
              />
            )}

            {/* 채용공고 목록 */}
            <JobList
              jobs={filteredJobs}
              onBookmark={handleBookmark}
              searchQuery={searchQuery}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobBoardPage;
